import React, { useEffect, useState } from "react";
import LandingCard from "../reutilizableLanding/LandingCard";
import { Oferta } from "../types/landingSections";
import { useProfesional } from "../context/ProfesionalContext";
import { safeFetch } from "@/api/safeFetch";
import CarouselBlock from "../util/CarouselBlock";

type BlockProps = {
  items: Oferta[];
  styles?: any;
};

/* =========================
   GRID BLOCK (SMALL CARDS)
   -> hasta 6 por fila
========================= */
const GridBlock: React.FC<BlockProps> = ({ items, styles }) => {
  const getImages = (o: Oferta) =>
    Array.isArray(o.imagenes) ? o.imagenes.filter(Boolean) : [];

  return (
    <section className="w-full gap-2">
      <div
        className="
          grid
          grid-cols-2
          sm:grid-cols-3
          md:grid-cols-4
          lg:grid-cols-5
          xl:grid-cols-6
          gap-5
        "
      >
          <div className="flex flex-wrap justify-start lg:px-4">
          {items.map((o) => (
            <div key={o.id} className="w-[175px]">
              <LandingCard
                titulo={o.titulo}
                descripcion={o.descripcion}
                precio={o.precio ?? 0}
                imagenes={getImages(o)}
                styles={styles}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* =========================
   WIDE BLOCK (2x ANCHO)
   -> hasta 4 por fila
========================= */
const WideBlock: React.FC<BlockProps> = ({ items, styles }) => {
  const getImages = (o: Oferta) =>
    Array.isArray(o.imagenes) ? o.imagenes.filter(Boolean) : [];

  return (
    <section className="w-full gap-2 pt-4 lg:px-2">
      <div
        className="
          grid
          grid-cols-1
          sm:grid-cols-2
          lg:grid-cols-3
          xl:grid-cols-4
          gap-5
        "
      >
        {items.map((o) => (
          <LandingCard
            key={o.id}
            variant="wide"
            titulo={o.titulo}
            descripcion={o.descripcion}
            precio={o.precio ?? 0}
            imagenes={getImages(o)}
            styles={styles}
          />
        ))}
      </div>
    </section>
  );
};

/* =========================
   MAIN COMPONENT
========================= */
const OfertasLanding: React.FC = () => {
  const { perfilId, landingData } = useProfesional();
  const [ofertas, setOfertas] = useState<Oferta[]>([]);
  const [loading, setLoading] = useState(true);

  const styles = landingData?.theme?.globalStyles;

  useEffect(() => {
    if (!perfilId) return;

    const fetchOfertas = async () => {
      try {
        setLoading(true);

        const res = await safeFetch(`/api/landingOfertas/${perfilId}/ofertas`);

        const json = await res.json();
        setOfertas(json.data ?? []);
      } catch (err) {
        console.error("Error cargando ofertas:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOfertas();
  }, [perfilId]);

  const carousel = ofertas.filter((o) => o.layout === "carousel");
  const grid = ofertas.filter((o) => o.layout === "grid");
  const wide = ofertas.filter((o) => o.layout === "wide");

  if (loading) {
    return <div className="text-white p-10">Cargando ofertas...</div>;
  }

  if (!ofertas.length) {
    return (
      <div className="w-full flex justify-center pt-10 md:px-4">
        <div className="w-full max-w-5xl rounded-3xl border border-white/10 bg-white/5 p-10 text-center text-white/70">
          Todavía no hay ofertas añadidas.
        </div>
      </div>
    );
  }

  return (
    <div
      className="
        w-full
        flex flex-col
        gap-5
        pt-5
        px-4
        max-w-7xl
        mx-auto
        pb-20
      
      "
    >
      {/* CAROUSEL */}
      {carousel.length > 0 && (
        <CarouselBlock items={carousel} styles={styles}/>
      )}

      {grid.length > 0 && <GridBlock items={grid} styles={styles} />}

      {wide.length > 0 && <WideBlock items={wide} styles={styles} />}
    </div>
  );
};

export default OfertasLanding;
